import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import PromisePool from 'es6-promise-pool';
import { connect } from 'react-redux';
import _ from 'lodash';
import {
  Button,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { updateFile } from '../../actions/Files';
import { updateShareFile } from '../../actions/Shares';

import { APIcreateFilesGroup } from '../../api/Files';

import FileStatusSelect from '../Upload/FileStatusSelect';

import { userHasFeature } from '../../helpers/Features';
import { getSetting } from '../../helpers/Settings';

import ProcessingSpinner from '../ProcessingSpinner';
import ErrorBoundary from '../ErrorBoundary';

const StatusModal = ({
  statuses,
  share,
  files,
  handleClose,
  updateFileAction,
  updateShareFileAction,
}) => {
  const intl = useIntl();

  const getStatusFromFiles = () => {
    const fileStatuses = _.uniqWith(files.map(f => (f.status)), _.isEqual);
    if (fileStatuses.length === 0 || fileStatuses.length > 1) {
      return null;
    }
    return fileStatuses[0];
  };

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [editedStatus, setEditedStatus] = useState(getStatusFromFiles());
  const [saveButtonText, setSaveButtonText] = useState('Save');
  const [savedFiles, setSavedFiles] = useState(0);

  useEffect(() => {
    if (saving && files.length > 1) {
      setSaveButtonText(
        intl.formatMessage(
          { defaultMessage: 'Saved {savedFiles} of {filesCount} files...' },
          { savedFiles, filesCount: files.length },
        ),
      );
    } else {
      setSaveButtonText(intl.formatMessage({ defaultMessage: 'Save' }));
    }
  }, [saving, savedFiles, files.length, intl]);

  const handleChange = (e) => {
    const { value } = e.target;
    const newStatus = statuses.find(s => (s.id === value));
    if (newStatus) {
      setEditedStatus(newStatus);
    } else {
      setEditedStatus({
        value: '',
        label: intl.formatMessage({ defaultMessage: '-- no status --' }),
        color: null,
      });
    }
  };

  const handleSaveFiles = (uploadUuid) => {
    let counter = 0;
    const promiseProducer = () => {
      if (counter < files.length) {
        const file = files[counter];
        counter += 1;

        file.status = editedStatus;
        file.uploadUuid = uploadUuid;

        if (share) {
          return updateShareFileAction(share.uuid, file)
            .then(() => {
              setSavedFiles(counter);
            })
            .catch(() => {
              setError(true);
            });
        }
        return updateFileAction(file).then(() => {
          setSavedFiles(counter);
        });
      }
      return null;
    };

    const pool = new PromisePool(promiseProducer, 2);
    pool.start().then(() => {
      setSaving(false);
      setSavedFiles(0);
      if (!error) {
        handleClose();
      }
    });
  };

  const handleSubmit = () => {
    setError(false);
    setSaving(true);

    APIcreateFilesGroup({ type: 'edit_files' }).then((response) => {
      const { data } = response;
      if (data) {
        handleSaveFiles(data.uuid);
      }
    });
  };

  const statusRequired = userHasFeature('file-status') && getSetting('file_status_required');
  const statusError = statusRequired && (!editedStatus || editedStatus.value?.length === 0);

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose} className="modal-header-center">
        {intl.formatMessage({ defaultMessage: 'Change Status' })}
      </ModalHeader>
      <ModalBody className="modal-status-body">
        {files.length > 1 && (
          <p className="modal-body-center">
            {intl.formatMessage(
              { defaultMessage: 'You are about to change the status for {filesCount} files' },
              { filesCount: files.length },
            )}
          </p>
        )}
        <div className="file-status-field">
          <FileStatusSelect
            defaultValue={editedStatus}
            isEdit={true}
            handleChange={handleChange}
            selectedFiles={files}
          />
        </div>
        {error && (
          <div className="invalid-feedback" style={{ display: 'block' }}>
            {intl.formatMessage({ defaultMessage: 'An error occured. Please try again later.' })}
          </div>
        )}
        {statusError && (
          <div className="invalid-feedback" style={{ display: 'block' }}>
            {intl.formatMessage({ defaultMessage: 'Status is required.' })}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose}>
          {intl.formatMessage({ defaultMessage: 'Close' })}
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={saving || statusError}>
          <ProcessingSpinner show={saving} /> {saveButtonText}
        </Button>
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapDispatchToProps = dispatch => ({
  updateFileAction: file => (
    dispatch(updateFile(file))
  ),
  updateShareFileAction: (shareUuid, file) => (
    dispatch(updateShareFile(shareUuid, file.uuid, { file }))
  ),
});

const mapStateToProps = state => ({
  statuses: state.Files.statuses,
  share: state.Shares.share,
});

StatusModal.propTypes = {
  statuses: PropTypes.array.isRequired,
  files: PropTypes.array.isRequired,
  share: PropTypes.object,
  handleClose: PropTypes.func.isRequired,
  updateFileAction: PropTypes.func.isRequired,
  updateShareFileAction: PropTypes.func.isRequired,
};

StatusModal.defaultProps = {
  share: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(StatusModal);
